import { useEffect, useRef, useState } from 'react';
import { PHOTO_LOOKS, type PhotoLook } from '@qq/shared';
import {
  applyLook,
  cameraSupported,
  fileToSquareCanvas,
  toDataUrl,
  toSquareCanvas,
} from '../lib/photo.js';

/**
 * Taking a selfie to become your character.
 *
 * Two ways in, because one of them is not always available: the live camera via
 * getUserMedia, and a plain file input with `capture="user"` which opens the
 * iPad's own camera app. The fallback is not decoration - a home-screen web app
 * on iOS has historically been the flakiest place to ask for getUserMedia, and
 * a five-year-old cannot debug a permissions prompt.
 *
 * The picture never leaves the device until the child taps Keep, and even then
 * it goes only to the family's own server.
 */

interface SelfieCaptureProps {
  onSave: (dataUrl: string) => void;
  onCancel: () => void;
  saving?: boolean;
  error?: string | null;
}

export function SelfieCapture({ onSave, onCancel, saving, error }: SelfieCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const captured = useRef<HTMLCanvasElement | null>(null);

  const [live, setLive] = useState(false);
  const [cameraProblem, setCameraProblem] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [look, setLook] = useState<PhotoLook>('natural');
  const [counting, setCounting] = useState<number | null>(null);

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setLive(false);
  };

  // Whatever happens, the camera light goes out when this screen goes away.
  useEffect(() => stopCamera, []);

  const startCamera = async () => {
    setCameraProblem(null);
    if (!cameraSupported()) {
      setCameraProblem('This device wants to use its own camera app instead.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1024 }, height: { ideal: 1024 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setLive(true);
    } catch {
      setCameraProblem('This device wants to use its own camera app instead.');
    }
  };

  const shoot = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = toSquareCanvas(video, video.videoWidth, video.videoHeight);
    captured.current = canvas;
    setPreview(toDataUrl(applyLook(cloneCanvas(canvas), look)));
    stopCamera();
  };

  const countdownThenShoot = () => {
    setCounting(3);
    let n = 3;
    const tick = window.setInterval(() => {
      n -= 1;
      if (n <= 0) {
        window.clearInterval(tick);
        setCounting(null);
        shoot();
      } else {
        setCounting(n);
      }
    }, 900);
  };

  const chooseFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const canvas = await fileToSquareCanvas(file);
      captured.current = canvas;
      setPreview(toDataUrl(applyLook(cloneCanvas(canvas), look)));
    } catch {
      setCameraProblem('That picture could not be opened. Try taking another one.');
    }
  };

  const changeLook = (next: PhotoLook) => {
    setLook(next);
    if (captured.current) setPreview(toDataUrl(applyLook(cloneCanvas(captured.current), next)));
  };

  const retake = () => {
    captured.current = null;
    setPreview(null);
    setLook('natural');
  };

  return (
    <section className="selfie">
      <h2>Make a photo character</h2>

      {!preview && (
        <>
          <div className="selfie-stage">
            <video ref={videoRef} playsInline muted className={live ? '' : 'is-hidden'} />
            {!live && (
              <div className="selfie-placeholder" aria-hidden="true">
                <span>📷</span>
              </div>
            )}
            {counting !== null && <div className="selfie-count">{counting}</div>}
          </div>

          {cameraProblem && <p className="gentle-note">{cameraProblem}</p>}

          <div className="selfie-actions">
            {!live ? (
              <button type="button" className="btn" onClick={() => void startCamera()}>
                Turn on the camera
              </button>
            ) : (
              <button
                type="button"
                className="btn"
                onClick={countdownThenShoot}
                disabled={counting !== null}
              >
                Take the photo
              </button>
            )}

            {/* Always offered: on an iPad this opens the camera app itself. */}
            <label className="btn btn--outline">
              Use the camera app
              <input
                type="file"
                accept="image/*"
                capture="user"
                className="is-visually-hidden"
                onChange={(e) => void chooseFile(e.target.files?.[0])}
              />
            </label>
          </div>
        </>
      )}

      {preview && (
        <>
          <div className="selfie-stage">
            <img src={preview} alt="Your new character" />
          </div>

          <p>Pick a look. All of this happens right here on your iPad.</p>
          <div className="look-row">
            {PHOTO_LOOKS.map((option) => (
              <button
                key={option.key}
                type="button"
                className="chip"
                data-selected={look === option.key}
                onClick={() => changeLook(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {error && <p className="gentle-note">{error}</p>}

          <div className="selfie-actions">
            <button type="button" className="btn" disabled={saving} onClick={() => onSave(preview)}>
              {saving ? 'Saving…' : 'Keep this one'}
            </button>
            <button type="button" className="btn btn--outline" onClick={retake} disabled={saving}>
              Take another
            </button>
          </div>
        </>
      )}

      <button type="button" className="btn btn--quiet" onClick={onCancel}>
        Go back
      </button>
    </section>
  );
}

function cloneCanvas(source: HTMLCanvasElement): HTMLCanvasElement {
  const copy = document.createElement('canvas');
  copy.width = source.width;
  copy.height = source.height;
  copy.getContext('2d')?.drawImage(source, 0, 0);
  return copy;
}
